/**
 * rasterSampler.ts
 * يقرأ قيمة الطقس مباشرةً من نُسج GFS الرمادية المحلّية (public/rasters/*.png) عند أي
 * إحداثي — بلا أي طلب لـ Open-Meteo. هذا يُلغي اعتماد التلميح/الرياح على الـ API
 * (وينهي أخطاء 429 نهائياً) مادام مصدر الطبقات هو نفسه نُسج NOAA GFS المجانية.
 *
 * التطبيع معكوس تماماً لما يكتبه gfs_to_raster.py:
 *   - السلَّمية: القيمة الرمادية g∈[0,255] →  value = vmin + (g/255)·(vmax−vmin)
 *   - الرياح (RGB): R=السرعة، G=U، B=V  (مطابق WIND_UV_MAX / WIND_SPEED_MAX).
 *
 * هندسة الصورة (مطابقة للسكربت والشيدر): الصفّ 0 (أعلى الصورة) = الجنوب، والعمود 0 =
 * خط الطول −180.
 */

import type { ForecastGridType } from '../config/weatherLayers';
import { VALUE_RANGES, WIND_UV_MAX } from '../components/WeatherMap/webgl/weatherTextures';
import { queuePrefetch } from './prefetchQueue';
import { perfSection } from './perfProbe';
import { layerFrameUrl, setLayerRasterSource, whenRasterVersionKnown, type LayerRasterSource } from './rasterVersion';
import { dropRasterImage, loadRasterImage, putRasterImage } from './rasterImageCache';

const WIND_SPEED_MAX = 120; // قناة R في نسيج RGB المصغَّر — يطابق WIND_SPEED_MAX في السكربت

interface RasterField {
    width: number;
    height: number;
    data: Uint8ClampedArray; // RGBA
}

export interface WindSample {
    u: number;
    v: number;
    speed: number;
    direction: number; // اتجاه المصدر (meteorological) بالدرجات
}

function normalizeLongitude(value: number): number {
    return ((((value + 180) % 360) + 360) % 360) - 180;
}

/** سقف الكاش: كل حقل ImageData بالدقة الكاملة (~1-4MB) — بلا سقف كان يتضخّم أثناء التشغيل. */
const MAX_FIELDS = 16;

/*
 * ── الفكّ في عامل ─────────────────────────────────────────────────────────────
 *
 * راجع رأس rasterDecodeWorker.ts للقياس. الصورة المفكوكة في كاش loadRasterImage
 * مشتركة بين عدّة مستهلكين، فلا تُنقل هي: `createImageBitmap(img)` يصنع نسخةً على
 * الـGPU بلا فكٍّ جديد، وتلك تُنقل ملكيّةً إلى العامل ويُغلقها هو.
 *
 * وأيّ فشل — متصفّحٌ بلا OffscreenCanvas، أو عاملٌ تعذّر إنشاؤه — يسقط إلى الفكّ
 * القديم على الخيط الرئيسي: أبطأ، لكن القيمة الصحيحة لا تغيب.
 */
let decodeWorker: Worker | null | undefined;
let decodeSeq = 0;
const decodeWaiting = new Map<number, { resolve: (f: RasterField) => void; reject: (e: unknown) => void }>();

function getDecodeWorker(): Worker | null {
    if (decodeWorker !== undefined) return decodeWorker;
    try {
        if (typeof OffscreenCanvas === 'undefined' || typeof Worker === 'undefined') throw new Error('unsupported');
        decodeWorker = new Worker(
            new URL('../components/WeatherMap/workers/rasterDecodeWorker.ts', import.meta.url),
            { type: 'module' },
        );
        decodeWorker.onmessage = (ev: MessageEvent<{ id: number; width?: number; height?: number; data?: Uint8ClampedArray; error?: string }>) => {
            const w = decodeWaiting.get(ev.data.id);
            if (!w) return;
            decodeWaiting.delete(ev.data.id);
            const { width, height, data, error } = ev.data;
            if (error || !data || !width || !height) w.reject(new Error(error ?? 'decode failed'));
            else w.resolve({ width, height, data });
        };
        decodeWorker.onerror = () => {
            for (const w of decodeWaiting.values()) w.reject(new Error('worker error'));
            decodeWaiting.clear();
            decodeWorker = null;                 // لا نعيد المحاولة: الاحتياط يكفي
        };
    } catch {
        decodeWorker = null;
    }
    return decodeWorker;
}

async function decodeOffThread(img: ImageBitmapSource): Promise<RasterField> {
    const worker = getDecodeWorker();
    if (!worker) throw new Error('no worker');
    const bitmap = await createImageBitmap(img);
    const id = ++decodeSeq;
    return new Promise<RasterField>((resolve, reject) => {
        decodeWaiting.set(id, { resolve, reject });
        worker.postMessage({ id, bitmap }, [bitmap]);
    });
}

class RasterSampler {
    private fields = new Map<string, RasterField>();
    private loading = new Map<string, Promise<RasterField | null>>();
    /** نُسج ثبت أنها غير موجودة (404) للنموذج الحالي — لا تُطلب ثانيةً. */
    private unavailable = new Set<string>();
    private dir = 'rasters/gfs/';
    /** هل يوفّر النموذج الحالي راستر wind-speed الرمادي كامل الدقّة؟ (من meta.layers).
     * نسيج الرياح RGB مصغَّر (خطوة 2) فقيمته تنحرف حتى ~25 كم عند السواحل — متى توفّر
     * الملف الكامل نقرأ السرعة منه، وإلا نسقط على قناة R المصغَّرة (دورات قديمة). */
    private windSpeedAvailable = false;

    setWindSpeedAvailable(v: boolean): void {
        this.windSpeedAvailable = v;
    }

    /**
     * خطوة النموذج الزمنية بالساعات (‏meta.step_hours): 1 لـGFS وICON، **3 لـECMWF**.
     *
     * ── لماذا تلزم في حساب التراكم ──────────────────────────────────────────────
     *
     * نُسج `precipitation` تخزّن **معدّلاً بـmm/h** (‏GFS: PRATE×3600، ECMWF: فرق tp ÷3).
     * وكان التراكم يجمع ثلاثة **إطارات** ويسمّي الناتج «مم خلال 3 ساعات».
     *
     * على GFS يصحّ الحساب بالمصادفة: الخطوة ساعة، فجمع ثلاثة معدّلات mm/h مضروبةً ضمناً
     * في ساعة واحدة = التراكم فعلاً. أمّا ECMWF فخطوته ثلاث ساعات، فالإطارات الثلاثة
     * تغطّي **تسع** ساعات لا ثلاثاً، والجمع بلا ضرب في Δt يُنقص الناتج **ثلاثة أضعاف**.
     * أي أن طبقة أمطار ECMWF كانت تعرض ثلث القيمة الحقيقية وتنسبها إلى مدّة خاطئة.
     *
     * الحلّ: نافذة **زمنية** ثابتة لا عدد إطارات ثابت — نأخذ ما يغطّي `hours` ساعة فعلية
     * ونضرب كل معدّل في خطوته.
     */
    private stepHours = 1;

    setStepHours(h: number): void {
        const next = Number.isFinite(h) && h > 0 ? h : 1;
        if (next === this.stepHours) return;
        this.stepHours = next;
        // إطارات التراكم المبنية بالخطوة القديمة صارت خاطئة القيمة — تُسقط ويُعاد بناؤها.
        for (const key of this.accumFrames.values()) dropRasterImage(key);
        this.accumFrames.clear();
        this.accumPending.clear();
        for (const listener of this.dirListeners) listener();
    }

    /**
     * عدد الإطارات التي تغطّي `hours` ساعة فعلية، ومعامل الضرب لكل إطار.
     * ‏GFS (خطوة 1، hours=3) ← 3 إطارات × 1؛ ECMWF (خطوة 3، hours=3) ← إطار واحد × 3.
     */
    private accumWindow(hours: number): { frames: number; weight: number } {
        const step = Math.max(1, this.stepHours);
        return { frames: Math.max(1, Math.round(hours / step)), weight: Math.min(step, hours) };
    }

    /** يتبع مجلد النموذج المختار (rasters/ ↔ rasters/ecmwf/ ↔ rasters/icon/) — تحدّده الواجهة. */
    setDir(dir: string): void {
        if (dir === this.dir) return;
        this.dir = dir;
        // الحقول القديمة تخصّ نموذجاً آخر — نفرغ الكاش كي لا تُقرأ قيم النموذج الخطأ.
        this.dirGen += 1;
        this.fields.clear();
        this.loading.clear();
        this.unavailable.clear();
        // نُسقط الصور المولَّدة من مخزنها أيضاً: هي بيانات الدورة السابقة، ولا مصدر لها
        // على الشبكة يُعيد بناءها، فتركها يعني احتلال سعة المخزن بإطارات لن تُطلب.
        for (const key of this.accumFrames.values()) dropRasterImage(key);
        this.accumFrames.clear();
        this.accumPending.clear();
        // الطبقات التي تقرأ من هنا (لا من رابط في خصائص React) لا وسيلة لها لتعرف أن
        // الكاش أُفرغ تحتها، فتبقى تعرض نتيجة النموذج السابق حتى يوقظها حدث لا علاقة له
        // بالموضوع (تحريك الخريطة، تبديل الساعة). نُعلمها صراحةً.
        for (const listener of this.dirListeners) listener();
    }

    /**
     * دورة نموذج جديدة على الخادم بنفس المجلّد.
     *
     * أسماء ملفّات الإطارات ثابتة (`temperature_012.png`) وتُستبدَل محتوياتها مع كل
     * دورة، فما في الكاش هنا يعود إلى الدورة السابقة بينما الشريط الزمني صار يُسنَد إلى
     * `run_epoch` الجديد — أي **نفس المؤشّر يعني ساعةً أخرى**. تركها يعني عرض قيم نهار
     * تحت تسمية ليل (فارق الدورتين 6 أو 12 ساعة). نُفرغها كما نفعل عند تبديل النموذج
     * تماماً: نفس السبب، مصدر مختلف.
     */
    resetForNewRun(): void {
        this.dirGen += 1;
        this.fields.clear();
        this.loading.clear();
        this.unavailable.clear();
        for (const key of this.accumFrames.values()) dropRasterImage(key);
        this.accumFrames.clear();
        this.accumPending.clear();
        for (const listener of this.dirListeners) listener();
    }

    /** رقم دورة المجلد: يتغيّر مع كل تبديل نموذج، وتلتقطه الطلبات الطائرة لتعرف أنها
     *  تخصّ نموذجاً انقضى. */
    private dirGen = 0;

    private dirListeners = new Set<() => void>();

    /** اشتراك في تبديل مجلد النموذج — يُعيد دالّة إلغاء الاشتراك. */
    onDirChange(listener: () => void): () => void {
        this.dirListeners.add(listener);
        return () => { this.dirListeners.delete(listener); };
    }

    /** هل ثبت أن نسيج هذه الطبقة/الإطار غير موجود للنموذج الحالي؟ يستخدمه المستدعي
     * للتمييز بين "لم يُحمَّل بعد" (انتظر) و"غير متاح إطلاقاً" (تجاوز الشرط). */
    isUnavailable(type: ForecastGridType | 'wind', idx: number): boolean {
        return this.unavailable.has(`${this.scalarFile(type as ForecastGridType)}_${idx}`);
    }

    /** اسم ملف القيمة السلَّمية للطبقة: الرياح تُقرأ من wind-speed كامل الدقّة متى توفّر. */
    private scalarFile(type: ForecastGridType): string {
        return type === 'wind' && this.windSpeedAvailable ? 'wind-speed' : type;
    }

    /**
     * طبقات لها مجلّد ومانيفست خاصّان بها لا مجلّد النموذج (اليوم: شذوذ سطح البحر).
     *
     * بدون هذا كان التلميح وتسميات المدن يطلبان `rasters/sst-anomaly_NNN.png` — مسار
     * لا وجود له. وخطورته أنه **لا يفشل بوضوح**: خادم التطوير (وأي SPA fallback) يردّ
     * بصفحة HTML بحالة 200، فتُغذّى إلى فاكّ الصور فيفشل صامتاً — قيمةٌ فارغة عند
     * التحويم بلا أي 404 في الشبكة يدلّ على السبب.
     */
    /** تُسجّلها الواجهة عند وصول مانيفست الطبقة المستقلّة؛ null يزيل التسجيل. */
    setLayerSource(type: ForecastGridType, source: LayerRasterSource | null): void {
        if (!setLayerRasterSource(type, source)) return;   // بلا تغيّر فعلي: لا تُفرغ شيئاً
        // الحقول المخزَّنة لهذه الطبقة جاءت من المسار القديم — نُسقطها كي لا تُقرأ قيم
        // مبنيّة على رابط لم يعد صالحاً (نفس منطق setDir).
        this.dirGen += 1;
        this.fields.clear();
        this.loading.clear();
        this.unavailable.clear();
        for (const listener of this.dirListeners) listener();
    }

    private url(type: string, idx: number): string {
        return layerFrameUrl(this.dir, type, idx);
    }

    /** يحمّل صورة في ImageData للإطار المطلوب تحديداً.
     * لا نعود إلى الإطار 000 عند فشل الإطار المطلوب: ذلك يضع بيانات وقت مختلف تحت
     * مؤشّر الوقت الحالي، وهو خطير خصوصاً للبرق وقد يجعله يظهر فوق مطر غير مطابق. */
    /**
     * قراءة حقل من الكاش **مع تحديث ترتيب استعماله** — أي LRU حقيقي.
     *
     * كان الطرد بترتيب **الإدراج** (‏`fields.keys().next()` بلا إعادة إدراج عند القراءة)،
     * أي FIFO لا LRU. والفرق ليس أكاديمياً هنا: تراكم الأمطار يقرأ الحقول idx-2..idx في
     * كل بناء، فحقلٌ يُقرأ ستّين مرّة في الثانية كان يُطرد لمجرّد أنه أُدرج قديماً — بينما
     * يبقى حقلٌ أُدرج للتوّ ولن يُطلب أبداً.
     *
     * أثره المقيس: القفزة اللونية عند **كل حدّ ساعة** أثناء التشغيل. قياس على تسجيل حديث
     * أعطى قفزات دورية بدقّة كل 3.08 ثانية — وهي `frameMs` نفسها — بحجم 0.089 مقابل وسيط
     * تغيّر 0.0018 (‏48 ضعفاً). سببها أن نسيج التراكم للساعة التالية لم يكتمل قبل العبور،
     * لأن أحد حقوله الثلاثة كان يُطرد ثم يُعاد تحميله وبناؤه من الصفر في كل دورة.
     */
    private takeField(key: string): RasterField | undefined {
        const f = this.fields.get(key);
        if (f) { this.fields.delete(key); this.fields.set(key, f); }
        return f;
    }

    private async loadField(type: string, idx: number): Promise<RasterField | null> {
        const key = `${type}_${idx}`;
        const cached = this.takeField(key);
        if (cached) return cached;
        if (this.unavailable.has(key)) return Promise.resolve(null);
        const inFlight = this.loading.get(key);
        if (inFlight) return inFlight;

        // ننتظر رقم الدورة قبل الجلب — وإلا خُزّن رابط بلا ?v يوماً كاملاً (قيم قديمة بالتلميح).
        const gen = this.dirGen;
        const task = whenRasterVersionKnown()
            .then(() => this.decode(this.url(type, idx)))
            .catch(() => null)
            .then((field) => {
                // بُدّل النموذج بينما كان هذا الطلب طائراً. المفاتيح لا تحمل المجلد، فلو
                // كتبنا الآن لوضعنا **بيانات النموذج القديم تحت مفتاح النموذج الجديد** —
                // فتُقرأ قيم خاطئة إلى أن يُطرد الحقل من الكاش. و404 في المجلد القديم كان
                // سيُسجَّل "غير متاح" فيُسكت الطبقة نفسها في المجلد الجديد حيث الملف موجود.
                // ولا نحذف من loading أيضاً: قد يكون فيه طلب جديد لنفس المفتاح فنُفقد تتبّعه.
                if (gen !== this.dirGen) return null;
                if (field) {
                    this.fields.set(key, field);
                    while (this.fields.size > MAX_FIELDS) {
                        const oldest = this.fields.keys().next().value;
                        if (oldest === undefined) break;
                        this.fields.delete(oldest);
                    }
                } else {
                    // نسيج غير موجود لهذا النموذج (404). بلا هذا السجل يعيد كل مستدعٍ
                    // طلبَه في كل إطار رسم — عشرات الطلبات الفاشلة في الثانية، وطبقة
                    // تنتظر إلى الأبد بيانات لن تصل.
                    this.unavailable.add(key);
                }
                this.loading.delete(key);
                return field;
            });
        this.loading.set(key, task);
        return task;
    }

    /**
     * الفكّ عبر الكاش المشترك: الملفّ نفسه كانت تطلبه ثلاثة مسارات مستقلّة وكلٌّ منها
     * ينشئ Image خاصّاً فيُفكّ من جديد (4 ميغابايت بكسلات لكل تكرار). loadRasterImage
     * يوحّد الطلبات الطائرة ويحتفظ بالصورة المفكوكة.
     */
    private decode(src: string): Promise<RasterField> {
        return loadRasterImage(src).then((img) =>
            decodeOffThread(img).catch(() => this.decodeHere(img)));
    }

    /** الفكّ على الخيط الرئيسي — احتياطٌ وحده حين لا يتوفّر العامل (راجع decodeOffThread). */
    private decodeHere(img: CanvasImageSource & { width: number; height: number }): RasterField {
        return perfSection('rasterSampler.decode(getImageData)', () => {
            const canvas = document.createElement('canvas');
            // ‏.width/.height: الكاش يُرجع ImageBitmap عادةً (وImage احتياطاً) — والخاصيتان
            // تساويان الأبعاد الأصلية على الطرفين، بخلاف .naturalWidth غير الموجود على الأول.
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) throw new Error('no 2d context');
            ctx.drawImage(img, 0, 0);
            const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
            return { width: canvas.width, height: canvas.height, data };
        });
    }

    /** يضمن تحميل نسيج طبقة/إطار مسبقاً (للاستدعاء من الخطافات قبل المرور).
     * يمرّ عبر طابور التزامن — إحماء غير عاجل يجب ألّا يغرق الاستضافة المشتركة. */
    prefetch(type: ForecastGridType | 'wind', idx: number): void {
        // طبقة الرياح تحتاج ملفّين: RGB (اتجاه/جسيمات) + wind-speed (قيمة التلميح واللون).
        const files = type === 'wind' && this.windSpeedAvailable ? ['wind', 'wind-speed'] : [type];
        for (const file of files) {
            const key = `${file}_${idx}`;
            if (this.fields.has(key) || this.loading.has(key)) continue;
            queuePrefetch(() => this.loadField(file, idx));
        }
        // الأمطار: نحمّل الساعتين السابقتين مسبقاً أيضاً — يلزمان لحساب التراكم الثلاثي
        // (sampleAccumulated) في التلميح فور وصول المؤشّر لهذه الساعة، بلا انتظار فكّ إضافي.
        if (type === 'precipitation') {
            for (const back of [1, 2]) {
                const h = idx - back;
                if (h < 0) continue;
                const key = `precipitation_${h}`;
                if (this.fields.has(key) || this.loading.has(key)) continue;
                queuePrefetch(() => this.loadField('precipitation', h));
            }
        }
    }

    /** يحمّل النسيج وينتظره — لبناء شبكة (مثل حقل الرياح) من الصورة. */
    async ensure(type: ForecastGridType | 'wind', idx: number): Promise<boolean> {
        return (await this.loadField(type, idx)) !== null;
    }

    /** فِهرسة بكسل مع لفّ خط الطول وقصّ خط العرض، وقراءة قناة channel. */
    private channelAt(field: RasterField, x: number, y: number, channel: number): number {
        const { width, height, data } = field;
        const xx = ((x % width) + width) % width;
        const yy = Math.max(0, Math.min(height - 1, y));
        return data[(yy * width + xx) * 4 + channel];
    }

    /**
     * تحويل lat/lon إلى إحداثي بكسل بنفس تراصف مركز البكسل الذي يستخدمه GPU
     * (`uv*gridSize - 0.5` في الشيدرات) — بدونه تنزاح القراءة عن اللون المرسوم
     * فعلياً بنصف بكسل تقريباً عند كل إحداثي، فيبدو "التلميح لا يطابق اللون".
     */
    private texelPos(field: RasterField, lat: number, lon: number): { x: number; y: number } {
        const { width, height } = field;
        const lonN = normalizeLongitude(lon);
        const u = (lonN + 180) / 360;
        const v = (lat + 90) / 180;
        return { x: u * width - 0.5, y: v * height - 0.5 };
    }

    /**
     * قراءة **نقطية** لقناة عند lat/lon → قيمة البايت الخام 0..255، بلا أي استيفاء.
     *
     * وُجدت لسؤال واحد لا يقبل المزج: «هل هذه الخليّة بيانات أم علامة لا-بيانات؟»
     * أي استيفاء يخلط العلامة بالقيم المجاورة فيُنتج رقماً وسطاً لا يساوي العلامة
     * ولا يساوي قيمةً حقيقية — فيضيع السؤال نفسه.
     */
    private nearest(field: RasterField, lat: number, lon: number, channel: number): number {
        const { x, y } = this.texelPos(field, lat, lon);
        return this.channelAt(field, Math.round(x), Math.round(y), channel);
    }

    /** استيفاء ثنائي الخطّية لقناة عند lat/lon → قيمة 0..255. (لطبقات القناع الساحلي.) */
    private bilinear(field: RasterField, lat: number, lon: number, channel: number): number {
        const { x: fx, y: fy } = this.texelPos(field, lat, lon);
        const x0 = Math.floor(fx);
        const y0 = Math.floor(fy);
        const dx = fx - x0;
        const dy = fy - y0;
        const c00 = this.channelAt(field, x0, y0, channel);
        const c10 = this.channelAt(field, x0 + 1, y0, channel);
        const c01 = this.channelAt(field, x0, y0 + 1, channel);
        const c11 = this.channelAt(field, x0 + 1, y0 + 1, channel);
        return (
            c00 * (1 - dx) * (1 - dy) +
            c10 * dx * (1 - dy) +
            c01 * (1 - dx) * dy +
            c11 * dx * dy
        );
    }

    // أوزان بيكيوبك Catmull-Rom — مطابقة تماماً لدالة cubicW في RasterHeatmapGLLayer/
    // HeatmapGLLayer (smoothVal/sampleSmooth، الاستيفاء الفعلي المُستخدَم الآن لكل الطبقات).
    // يجب أن تبقى متطابقة رياضياً حتى تُطابق قيمة التلميح لون البكسل الفعلي المرسوم على
    // الخريطة عند نفس الإحداثي بالضبط — لا تُبدَّل هنا وحدها بلا تبديل الشيدرات معها.
    private static cubicW(t: number): [number, number, number, number] {
        const t2 = t * t, t3 = t2 * t;
        return [
            -0.5 * t3 + t2 - 0.5 * t,
            1.5 * t3 - 2.5 * t2 + 1.0,
            -1.5 * t3 + 2.0 * t2 + 0.5 * t,
            0.5 * t3 - 0.5 * t2,
        ];
    }

    /**
     * استيفاء بيكيوبك (Catmull-Rom) **مقيَّد بمدى الجوار** لقناة عند lat/lon → 0..255.
     * يطابق smoothVal في شيدرات الطبقات — يمرّ بالقيم الفعلية بدل تنعيمها، فيجب أن
     * يُقرأ بنفس الأسلوب حتى يطابق رقم التلميح لون الخريطة تماماً.
     *
     * **لماذا التقييد:** لأوزان Catmull-Rom فصّان سالبان (الأوّل والرابع)، فعند قمّة
     * حادّة تتجاوز النتيجةُ **كل** قيم الشبكة المحيطة — أي تعرض رقماً غير موجود في
     * البيانات. قِيس على مقاطع تمثّل عين إعصار: قمّة شبكة 120 كم/س تعطي 130 في بُعد
     * واحد و**141 في بُعدين** (‏+18%). وهذا بالضبط ما رُصد: هبّات تقرأ 130–146 عندنا
     * بينما تقرأ أقل من 120 لدى مرجع يقرأ نفس نموذج GFS في نفس التوقيت.
     *
     * الأعاصير أشدّ قمم الحقل حدّةً (نصف قطر أقصى رياح 30–50 كم مقابل خليّة 27.8 كم)،
     * ولذلك يظهر الانحراف عندها تحديداً ويكاد يختفي في الحقول الهادئة.
     *
     * التقييد بأدنى/أقصى نافذة الـ4×4 يمنع اختراع قيمة خارج البيانات، ولا يمسّ النعومة
     * في أي مكان آخر: في الحقل المتدرّج تقع النتيجة داخل المدى أصلاً فلا يفعل شيئاً.
     */
    private bicubic(field: RasterField, lat: number, lon: number, channel: number): number {
        const { x: p, y: q } = this.texelPos(field, lat, lon);
        const ix = Math.floor(p), iy = Math.floor(q);
        const fx = p - ix, fy = q - iy;
        const wx = RasterSampler.cubicW(fx);
        const wy = RasterSampler.cubicW(fy);
        let sum = 0, lo = Infinity, hi = -Infinity;
        for (let j = 0; j < 4; j++) {
            for (let i = 0; i < 4; i++) {
                const v = this.channelAt(field, ix + i - 1, iy + j - 1, channel);
                sum += wx[i] * wy[j] * v;
                if (v < lo) lo = v;
                if (v > hi) hi = v;
            }
        }
        // تقييد بمدى الجوار (بيكيوبك مقيَّد) — راجع التعليق أعلى الدالة.
        return Math.min(hi, Math.max(lo, sum));
    }

    /**
     * كانت هذه الطبقات تُرسم بفصل ساحلي فتُقرأ ثنائية الخطّية لتطابقه. الفصل عُطِّل في
     * RasterHeatmapGLLayer (راجع التعليق عند u_useMask هناك)، فصارت كل الطبقات تُرسم
     * ببيكيوبك مقيَّد — ويجب أن تُقرأ به هنا أيضاً وإلّا افترق رقم التلميح عن لون البكسل.
     */

    /**
     * قيمة طبقة سلَّمية بوحدتها الأصلية عند lat/lon. يُرجع null إن لم يكن النسيج
     * محمّلاً بعد (يُطلَق تحميله ضمنياً، ويُحاول المستدعي ثانيةً في الإطار التالي).
     */
    sampleScalar(type: ForecastGridType, idx: number, lat: number, lon: number): number | null {
        const file = this.scalarFile(type);
        const field = this.takeField(`${file}_${idx}`);
        if (!field) { void this.loadField(file, idx); return null; }
        // الرياح بلا wind-speed (دورات قديمة): قناة R في RGB مرمَّزة على 0..120 لا VALUE_RANGES.
        const [vmin, vmax] = file === 'wind' ? [0, WIND_SPEED_MAX] : VALUE_RANGES[type];
        /*
         * ── «لا بيانات» ليست قيمة ──────────────────────────────────────────────
         *
         * راستر شذوذ سطح البحر يحجز البايت 0 للقارّات والجليد (‏NODATA_BYTE). وفكّ
         * الترميز الخطّي يحوّله إلى **-5.04 °م** — رقمٌ يبدو مشروعاً تماماً. وهذا ما
         * كان يُطبع فعلاً في شارات المدن: «لواندا ‎-5»، «أنتاناناريفو ‎-5»، وكلّها مدن
         * على اليابسة حيث لا شذوذ بحرٍ أصلاً. والأسوأ أن ‎-5 خارج مدى المفتاح المعروض
         * (‎-4..+4) فلا يجد القارئ له تفسيراً.
         *
         * القراءة **نقطية لا بيكيوبك** لهذا الفحص: البيكيوبك يمزج ستّ عشرة خليّة،
         * فخليّةُ صفرٍ واحدة تكفي لتشويه القيمة قرب السواحل بلا أن تُصفّرها. نفحص
         * الخليّة نفسها: إمّا بيانات أو لا.
         */
        if ((type === 'sst-anomaly' || type === 'sst-anomaly-rel')
            && this.nearest(field, lat, lon, 0) === 0) return null;
        const g = this.bicubic(field, lat, lon, 0); // القناة R
        return vmin + (Math.max(0, Math.min(255, g)) / 255) * (vmax - vmin);
    }

    /**
     * أعلى قيمة لطبقة سلَّمية داخل صندوق جغرافي، مع موقع الخليّة التي بلغتها.
     *
     * لماذا يلزم هذا بجانب sampleScalar: المستدعي (سرب البرق) يمسح الشاشة بشبكة بالبكسل
     * ويأخذ **عيّنة نقطية واحدة** لكل خليّة. هذا يصحّ عند التقريب حيث تغطّي خليّة الشاشة
     * أقلّ من خليّة راستر واحدة، لكنه ينهار عند التصغير: الخليّة العاصفية عرضها ثابت على
     * الأرض، فكلّما صُغِّرت الخريطة انكمشت إلى بضع بكسلات، وتمرّ نقاط الشبكة بجانبها كلها
     * فتُفقد الظاهرة تماماً. لا تُصلح ذلك شبكةٌ أدقّ: أي شبكة بالبكسل تخطئ ظاهرة أصغر من
     * تباعدها. الحلّ الوحيد الصحيح هو المسح **بدقّة الراستر نفسها** داخل رقعة كل خليّة —
     * فلا يعتمد الاكتشاف على التكبير إطلاقاً.
     *
     * تُقرأ القيم خاماً (بلا بيكيوبك): المطلوب "هل هنا ذروة نشاط" لا رقم مطابق للتلميح،
     * والاستيفاء هنا يعني ١٦ قراءة لكل خليّة راستر بلا فائدة.
     *
     * **بلا خطوة تخطٍّ ولا سقف عيّنات، عمداً.** أي تخفيف للمسح يعيد العيب نفسه من بابٍ
     * آخر: صندوقٌ يقرأ خليّة من كل ثلاث يخطئ عاصفةً بعرض خليّتين تماماً كما تخطئها
     * العيّنة النقطية. والتكلفة لا تحتاج سقفاً أصلاً لأنها **محدودة ذاتياً**: صناديق
     * المستدعي تُبلّط الشاشة بلا تداخل، فمجموع ما تقرؤه كل الصناديق معاً هو مساحة
     * الراستر تحت الشاشة — لا أكثر من الراستر كاملاً مهما بلغ التصغير. الحدّ الوحيد
     * الباقي هنا صريح: عدد الأعمدة/الصفوف مقصوص عند أبعاد الراستر، فمدىً شاذّ لخط الطول
     * (شاشة تعرض نسخ عالم مكرّرة) لا يضاعف العمل بلا نهاية.
     *
     * الإحداثيات المُعادة هي **مركز خليّة الراستر الفائزة** لا مركز الصندوق، فيقع الرمز
     * على الظاهرة نفسها لا على منتصف رقعة قد تكون في أغلبها صافية.
     */
    maxScalarInBox(
        type: ForecastGridType,
        idx: number,
        latMin: number, latMax: number,
        lonMin: number, lonMax: number,
    ): { value: number; lat: number; lon: number } | null {
        const file = this.scalarFile(type);
        const field = this.takeField(`${file}_${idx}`);
        if (!field) { void this.loadField(file, idx); return null; }
        const { width, height } = field;

        // بلا تسوية خط الطول: الصندوق قد يعبر ±180° بعد الإسقاط، والاستمرارية هنا أهمّ —
        // channelAt يلفّ المحور أفقياً بنفسه، وخط العرض يُقصّ عند القطبين.
        const xAt = (lon: number) => ((lon + 180) / 360) * width - 0.5;
        const yAt = (lat: number) => ((lat + 90) / 180) * height - 0.5;
        const x0 = Math.floor(xAt(lonMin));
        // نسخة عالم واحدة تكفي: ما بعدها تكرار للفّ نفسه، فقصّ العرض هنا لا يُفقد شيئاً.
        const x1 = Math.min(Math.ceil(xAt(lonMax)), x0 + width - 1);
        const y0 = Math.max(0, Math.floor(yAt(latMin)));
        const y1 = Math.min(height - 1, Math.ceil(yAt(latMax)));
        if (x1 < x0 || y1 < y0) return null;

        let bestRaw = -1, bestX = x0, bestY = y0;
        for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
                const v = this.channelAt(field, x, y, 0);   // القناة R
                if (v > bestRaw) { bestRaw = v; bestX = x; bestY = y; }
            }
        }
        if (bestRaw < 0) return null;

        const [vmin, vmax] = file === 'wind' ? [0, WIND_SPEED_MAX] : VALUE_RANGES[type];
        return {
            value: vmin + (bestRaw / 255) * (vmax - vmin),
            lat: ((bestY + 0.5) / height) * 180 - 90,
            lon: ((bestX + 0.5) / width) * 360 - 180,
        };
    }

    /**
     * مثل `maxScalarInBox` تماماً، لكن على الحقل **الممزوج زمنياً** بين الإطار idx والذي
     * يليه بنسبة `frac` — أي على الحقل الذي تعرضه طبقة الألوان في هذه اللحظة بالضبط.
     *
     * لماذا لا يكفي مزج ذروتَي الإطارين بعد حسابهما منفصلتين: الذروتان تقعان في خليّتين
     * مختلفتين عادةً (العاصفة تتحرّك)، فمزج القيمتين يعطي رقماً لا يقابله موضع، ويظلّ
     * على المستدعي أن يختار أحد الموضعين — فيقفز السرب كلّه دفعةً واحدة عند تجاوز
     * النسبة النصف. المزج هنا **قبل** أخذ الذروة: نمسح الرقعة مرّة واحدة ونحسب لكل
     * خليّة قيمتها الممزوجة، فتنتقل الذروة خليّةً خليّةً مع الزمن كما ينتقل اللون.
     *
     * إن لم يكن الإطار التالي محمَّلاً بعد نسقط على الإطار idx وحده (يُطلَق تحميله ضمنياً،
     * ويُصحَّح في إعادة البناء التالية) — أدقّ ما هو متاح، لا انتظار ولا شاشة فارغة.
     */
    maxScalarInBoxAt(
        type: ForecastGridType,
        idx: number, frac: number,
        latMin: number, latMax: number,
        lonMin: number, lonMax: number,
    ): { value: number; lat: number; lon: number } | null {
        const file = this.scalarFile(type);
        const fieldA = this.takeField(`${file}_${idx}`);
        if (!fieldA) { void this.loadField(file, idx); return null; }
        const t = Math.max(0, Math.min(1, frac));
        // النسيج التالي يُطلَق تحميله هنا أيضاً: بدونه يبقى المزج معطَّلاً إلى الأبد لأن
        // لا أحد غيرنا يطلبه لهذه الطبقة.
        let fieldB = t > 0 ? this.takeField(`${file}_${idx + 1}`) : undefined;
        if (t > 0 && !fieldB) void this.loadField(file, idx + 1);
        // أبعاد مختلفة تعني نسيجين من دورتين مختلفتين — لا يُمزجان.
        if (fieldB && (fieldB.width !== fieldA.width || fieldB.height !== fieldA.height)) fieldB = undefined;

        const { width, height } = fieldA;
        const xAt = (lon: number) => ((lon + 180) / 360) * width - 0.5;
        const yAt = (lat: number) => ((lat + 90) / 180) * height - 0.5;
        const x0 = Math.floor(xAt(lonMin));
        const x1 = Math.min(Math.ceil(xAt(lonMax)), x0 + width - 1);
        const y0 = Math.max(0, Math.floor(yAt(latMin)));
        const y1 = Math.min(height - 1, Math.ceil(yAt(latMax)));
        if (x1 < x0 || y1 < y0) return null;

        let bestRaw = -1, bestX = x0, bestY = y0;
        for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
                const a = this.channelAt(fieldA, x, y, 0);   // القناة R
                const v = fieldB ? a + (this.channelAt(fieldB, x, y, 0) - a) * t : a;
                if (v > bestRaw) { bestRaw = v; bestX = x; bestY = y; }
            }
        }
        if (bestRaw < 0) return null;

        const [vmin, vmax] = file === 'wind' ? [0, WIND_SPEED_MAX] : VALUE_RANGES[type];
        return {
            value: vmin + (bestRaw / 255) * (vmax - vmin),
            lat: ((bestY + 0.5) / height) * 180 - 90,
            lon: ((bestX + 0.5) / width) * 360 - 180,
        };
    }

    /**
     * تراكم قيمة سلَّمية عبر آخر `hours` ساعات ينتهي عندها الإطار idx — مثال: طبقة
     * الأمطار مصدرها PRATE (معدّل لحظي مم/س) لا حقلاً تراكمياً، فنشتقّ "تراكم 3 ساعات"
     * (أسلوب Windy في التلميح) بجمع القيمة اللحظية لكل ساعة من الساعات الثلاث (كلٌّ منها
     * يمثّل تقريباً مم الساعة تلك بافتراض خطوة ساعة واحدة بين الإطارات). يُرجع null إن لم
     * يكن أيّ إطار من النافذة محمَّلاً بعد (نفس اتفاقية sampleScalar: يُطلَق تحميله ضمنياً
     * ويُعاد المحاولة لاحقاً)، والساعات قبل بداية الشريط الزمني (idx-h < 0) تُحتسَب صفراً
     * لا أنها تُسقط التراكم (لا بيانات قبل "الآن" أصلاً).
     */
    sampleAccumulated(type: ForecastGridType, idx: number, lat: number, lon: number, hours: number): number | null {
        const { frames, weight } = this.accumWindow(hours);
        let sum = 0;
        for (let back = 0; back < frames; back += 1) {
            const h = idx - back;
            if (h < 0) continue;
            const v = this.sampleScalar(type, h, lat, lon);
            if (v === null) return null;
            // ‏v معدّل mm/h، وweight طول الفترة التي يمثّلها الإطار بالساعات.
            sum += v * weight;
        }
        return sum;
    }

    /**
     * كاش نُسج التراكم المُركَّبة — مفتاحه نوع/إطار/ساعات، وقيمته **المفتاح الصناعي**
     * (`mem:accum:…`) الذي سُجّلت تحته الصورة في كاش الصور المشترك.
     *
     * لم تعد قيمته `blob:` URL: صار البناء يُنتج ImageBitmap يُدرج مباشرةً في كاش الصور
     * (راجع getAccumulatedFrameUrl)، فلا رابط كائن يُنشأ ولا يحتاج إبطالاً — واختفى معه
     * مصدرُ تسريبٍ كان يلزمه تتبّع يدوي عند كل طرد وتفريغ.
     */
    private accumFrames = new Map<string, string>();
    /** طلبات بناء قيد التنفيذ — يمنع إطلاق بناء مكرَّر لنفس المفتاح بينما هو معلَّق. */
    private accumPending = new Set<string>();

    /**
     * يبني نسيجاً (blob: URL) يمثّل **تراكم القيمة عبر آخر `hours` ساعات** بدل معدّلها
     * اللحظي — ليُلوَّن به هيتماب الطبقة مباشرةً (RasterHeatmapGLLayer)، فيطابق لون
     * الخريطة نفس الرقم المعروض في التلميح (sampleAccumulated) تماماً؛ بدلاً من تلوين
     * الخريطة بالمعدّل اللحظي بينما يعرض التلميح تراكماً — وهو ما كان يجعل نقطة "62 مم
     * (3h)" تُرسم بلون معتدل بدل الأبيض الذي يناسب هذه الشدّة أصلاً.
     *
     * يُرجع null إن لم تصل بعد إحدى نُسج الساعات اللازمة (نفس اتفاقية sampleAccumulated:
     * التحميل يُطلَق ضمنياً عبر sampleScalar، ويُعاد المحاولة في الإطار التالي) **أو** ريثما
     * يكتمل ترميز الإطار الحالي (انظر أدناه). النتيجة تُخزَّن مرّة واحدة لكل (نوع/إطار/ساعات).
     *
     * كانت هذه الدالة تستدعي canvas.toDataURL() المتزامن — ترميز PNG base64 لنسيج
     * ~1440×721 على Main Thread، وهو ما قِيس فعلياً كسبب التقطّع الحادّ عند تحريك شريط
     * الأمطار (بعكس الحرارة التي تبدّل رابط صورة جاهزة بلا أي ترميز). canvas.toBlob()
     * يؤدّي نفس الترميز **خارج** Main Thread في Chrome/Firefox، فتبقى الواجهة سلسة أثناء
     * البناء؛ الكلفة الوحيدة المتبقّية على الخيط الرئيسي هي حلقة الجمع نفسها (~1 مليون
     * بكسل)، وهي أخفّ من الترميز بعشرات المرّات. الثمن: النتيجة تصل بعد دورة/دورتين لا
     * فوراً، فتُرجع الدالة null حتى تجهز (يستمر hook الاستدعاء في إعادة المحاولة كما كان).
     */
    getAccumulatedFrameUrl(type: ForecastGridType, idx: number, hours: number): string | null {
        const cacheKey = `${type}_${idx}_${hours}`;
        const cached = this.accumFrames.get(cacheKey);
        if (cached) return cached;
        if (this.accumPending.has(cacheKey)) return null;

        const file = this.scalarFile(type);
        const { frames, weight } = this.accumWindow(hours);
        const usedFields: RasterField[] = [];
        for (let back = 0; back < frames; back += 1) {
            const h = idx - back;
            if (h < 0) continue;
            const field = this.takeField(`${file}_${h}`);
            if (!field) { void this.loadField(file, h); return null; }
            usedFields.push(field);
        }
        if (usedFields.length === 0) return null;

        const { width, height } = usedFields[0];
        const [vmin, vmax] = VALUE_RANGES[type];
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        const out = ctx.createImageData(width, height);
        const pixels = width * height;
        for (let p = 0; p < pixels; p += 1) {
            const i = p * 4;
            let sum = 0;
            // نفس معادلة sampleAccumulated بالضبط كي يطابق لونُ الخريطة رقمَ التلميح.
            for (const field of usedFields) sum += (vmin + (field.data[i] / 255) * (vmax - vmin)) * weight;
            const norm = Math.max(0, Math.min(255, Math.round((sum / vmax) * 255)));
            out.data[i] = norm; out.data[i + 1] = norm; out.data[i + 2] = norm; out.data[i + 3] = 255;
        }
        ctx.putImageData(out, 0, 0);

        /*
         * **بلا ترميز PNG إطلاقاً.**
         *
         * كان المسار: ImageData ← ترميز PNG (toBlob) ← blob URL ← جلب ← فكّ PNG ← رفع
         * إلى GPU. أي أننا نرمّز مليون بكسل لنفكّها فوراً بأنفسنا — رحلة كاملة بلا أي
         * فائدة، وهي سبب كون الأمطار أبطأ الطبقات تنقّلاً بفارق واضح عن الحرارة (الحرارة
         * تبدّل رابط ملفّ جاهز، والأمطار تبني إطارها من الصفر مرّتين).
         *
         * `createImageBitmap(ImageData)` يعطي كائناً جاهزاً للرفع مباشرةً، ونُدرجه في كاش
         * الصور تحت مفتاح صناعي فيصل إلى الطبقة عبر نفس مسار الروابط بلا أي تغيير فيها.
         * يبقى غير متزامن (لذلك ما زلنا نُرجع null ونُعيد المحاولة) لكنه يكلّف كسراً من
         * زمن الترميز.
         */
        this.accumPending.add(cacheKey);
        const dirAtRequest = this.dirGen;
        const key = `mem:accum:${type}:${idx}:${hours}`;
        void createImageBitmap(out).then((bitmap) => {
            this.accumPending.delete(cacheKey);
            // النموذج تغيّر أثناء البناء — هذا الإطار لم يعد يخصّ الدورة الحالية.
            if (dirAtRequest !== this.dirGen) { bitmap.close(); return; }
            putRasterImage(key, bitmap);
            if (this.accumFrames.size >= MAX_FIELDS) {
                const oldestKey = this.accumFrames.keys().next().value;
                if (oldestKey !== undefined) {
                    dropRasterImage(this.accumFrames.get(oldestKey)!);
                    this.accumFrames.delete(oldestKey);
                }
            }
            this.accumFrames.set(cacheKey, key);
        }).catch(() => { this.accumPending.delete(cacheKey); });
        return null;
    }

    /**
     * سرعة/اتجاه الرياح عند lat/lon. null إن لم تُحمَّل النُسج بعد (يُطلَق تحميلها).
     *
     * **المقدار يُقرأ من راستر wind-speed كامل الدقّة** لا من مركّبتي U/V:
     *  - نسيج الرياح RGB مصغَّر (WIND_STRIDE)، فقيمته تنحرف عند التضاريس الحادّة حتى
     *    ~25 كم — وهو ما جعل نقطة على حافّة عسير تقرأ 51 كم/س بينما القيمة الحقيقية 22
     *    (نقطة البحر المجاورة نُسبت إلى الجبل). فوق البحر المفتوح لا يظهر الأثر لأن
     *    الحقل هناك ناعم.
     *  - U و V مقصوصتان عند ±60 كم/س عند التوليد، فمقدارهما يتشبّع عند 84.9 كم/س
     *    ويقصّر كل رياح أقوى — بينما مدى راستر السرعة 229.5.
     *  - والأهمّ: بهذا يطابق رقم التلميح **لون الخريطة ولوحة التوقّع** بالضبط، فكلاهما
     *    يقرأ الملف نفسه بنفس الاستيفاء (bicubic).
     * الاتجاه يبقى من U/V (لا مصدر آخر له)، وهو أقلّ حساسية للتصغير من المقدار.
     */
    sampleWind(idx: number, lat: number, lon: number): WindSample | null {
        // لا سقوط إلى wind_0: كان يعرض رياح الساعة صفر منسوبةً إلى الساعة المطلوبة.
        const vec = this.takeField(`wind_${idx}`);
        if (!vec) { void this.loadField('wind', idx); return null; }
        const g = this.bilinear(vec, lat, lon, 1); // U في القناة G
        const b = this.bilinear(vec, lat, lon, 2); // V في القناة B
        const u = (g / 255 - 0.5) * 2 * WIND_UV_MAX;
        const v = (b / 255 - 0.5) * 2 * WIND_UV_MAX;
        const direction = (270 - (Math.atan2(v, u) * 180) / Math.PI + 360) % 360;

        let speed: number;
        if (this.windSpeedAvailable) {
            const full = this.takeField(`wind-speed_${idx}`);
            if (!full) { void this.loadField('wind-speed', idx); return null; }
            const [vmin, vmax] = VALUE_RANGES.wind;
            const raw = this.bicubic(full, lat, lon, 0);
            speed = vmin + (Math.max(0, Math.min(255, raw)) / 255) * (vmax - vmin);
        } else {
            // دورات قديمة بلا الملف الكامل: لا مصدر آخر للمقدار.
            speed = Math.sqrt(u * u + v * v);
        }
        return { u, v, speed, direction };
    }

    /** أقصى مدى للسرعة (للتطبيع) — يطابق نسق النسيج. */
    get windSpeedMax(): number { return WIND_SPEED_MAX; }
}

export const rasterSampler = new RasterSampler();
